import apiClient from '../api/client';

export interface PublicOrganization {
  slug: string;
  name: string;
}

const FETCH_TIMEOUT_MS = 3000;

/**
 * Spec 026 R3 — list all active organisations as `{slug, name}` pairs.
 *
 * Used by the OrgPicker fallback in the ProfileSetupStep when the user
 * reaches `/register` directly (no Welcome-driven slug in wizard state).
 *
 * Returns `[]` for any non-success outcome (network error, timeout, 5xx).
 * Callers render an empty-state error in that case.
 */
export async function listAll(
  signal?: AbortSignal,
): Promise<PublicOrganization[]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const { data } = await apiClient.get<{ organizations: PublicOrganization[] }>(
      '/api/public/organizations',
      { signal: controller.signal },
    );
    return data.organizations ?? [];
  } catch {
    return [];
  } finally {
    clearTimeout(timeoutId);
  }
}
